import { Button, Field, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { hrApi, hrAttendanceApi, type PersonnelFileRow, type PositionRow } from "../../api.js";
import { askReason, ErrorLine, useAction } from "../common.js";

const IRANIAN_DAYS = [
  { dayOfWeek: 0, label: "شنبه" },
  { dayOfWeek: 1, label: "یکشنبه" },
  { dayOfWeek: 2, label: "دوشنبه" },
  { dayOfWeek: 3, label: "سه‌شنبه" },
  { dayOfWeek: 4, label: "چهارشنبه" },
  { dayOfWeek: 5, label: "پنج‌شنبه" },
  { dayOfWeek: 6, label: "جمعه" },
] as const;

export function ShiftsTab() {
  const [showAddShiftModal, setShowAddShiftModal] = useState(false);
  const [showExceptionModal, setShowExceptionModal] = useState(false);

  // Shift Template Form State
  const [shiftName, setShiftName] = useState("");
  const [startTime, setStartTime] = useState("06:00");
  const [endTime, setEndTime] = useState("14:00");
  const [color, setColor] = useState("#3b82f6");
  const [minCountMap, setMinCountMap] = useState<Record<string, number>>({});

  // Exception Form State
  const [excPersonId, setExcPersonId] = useState("");
  const [excDate, setExcDate] = useState(new Date().toISOString().slice(0, 10));
  const [excKind, setExcKind] = useState<"off" | "shift" | "replacement">("off");
  const [excShiftId, setExcShiftId] = useState("");
  const [excReplacementId, setExcReplacementId] = useState("");
  const [excReason, setExcReason] = useState("");

  const templatesQuery = useQuery({
    queryKey: ["hr", "shift-templates"],
    queryFn: () => hrAttendanceApi.listShiftTemplates(),
  });

  const staffQuery = useQuery({
    queryKey: ["hr", "people"],
    queryFn: () => hrApi.listPeople(),
  });

  const positionsQuery = useQuery({
    queryKey: ["hr", "positions"],
    queryFn: () => hrApi.listPositions(),
  });

  const patternsQuery = useQuery({
    queryKey: ["hr", "weekly-patterns"],
    queryFn: () => hrAttendanceApi.listWeeklyShiftPatterns(),
  });

  const exceptionsQuery = useQuery({
    queryKey: ["hr", "shift-exceptions"],
    queryFn: () => hrAttendanceApi.listShiftExceptions(),
  });

  const addShiftAction = useAction(async () => {
    const minHeadcount = Object.entries(minCountMap)
      .filter(([, count]) => count > 0)
      .map(([posId, minCount]) => {
        const pos = positionsQuery.data?.find((p) => p.id === posId);
        return {
          positionId: posId,
          positionName: pos?.name || "",
          minCount,
        };
      });

    await hrAttendanceApi.createShiftTemplate({
      name: shiftName.trim(),
      startTime,
      endTime,
      color,
      notes: "",
      minHeadcount,
    });

    setShowAddShiftModal(false);
    setShiftName("");
    setStartTime("06:00");
    setEndTime("14:00");
    setMinCountMap({});
  }, [["hr", "shift-templates"]]);

  const archiveShiftAction = useAction(
    async ({ id, version, name }: { id: string; version: number; name: string }) => {
      const reason = askReason(`الگوی شیفت ${name}`);
      if (!reason) return;
      await hrAttendanceApi.archiveShiftTemplate(id, version, reason);
    },
    [["hr", "shift-templates"]],
  );

  const savePatternAction = useAction(
    async ({
      personId,
      days,
    }: {
      personId: string;
      days: Array<{ dayOfWeek: number; shiftTemplateId: string | null }>;
    }) => {
      await hrAttendanceApi.setWeeklyShiftPattern(personId, days);
    },
    [["hr", "weekly-patterns"]],
  );

  const addExceptionAction = useAction(async () => {
    await hrAttendanceApi.createShiftException({
      personId: excPersonId,
      date: excDate,
      kind: excKind,
      shiftTemplateId: excKind !== "off" && excShiftId ? excShiftId : null,
      replacementForPersonId: excKind === "replacement" && excReplacementId ? excReplacementId : null,
      reason: excReason.trim(),
    });

    setShowExceptionModal(false);
    setExcReason("");
  }, [
    ["hr", "shift-exceptions"],
    ["hr", "shift-plan"],
  ]);

  const archiveExceptionAction = useAction(
    async ({ id, version }: { id: string; version: number }) => {
      await hrAttendanceApi.archiveShiftException(id, version, "لغو توسط کاربر");
    },
    [
      ["hr", "shift-exceptions"],
      ["hr", "shift-plan"],
    ],
  );

  const templates = templatesQuery.data ?? [];
  const staff: PersonnelFileRow[] = staffQuery.data ?? [];
  const positions: PositionRow[] = positionsQuery.data ?? [];
  const patterns = patternsQuery.data ?? [];
  const exceptions = exceptionsQuery.data ?? [];

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* ---------- Shift Templates Section ---------- */}
      <Panel title="الگوهای شیفت کاری">
        <div
          className="row wrap"
          style={{ justifyContent: "space-between", alignItems: "center", marginBottom: "var(--s3)" }}
        >
          <p className="muted" style={{ margin: 0 }}>
            تعریف بازه‌های زمانی شیفت‌ها (مانند صبح و عصر) و حداقل پرسنل مورد نیاز برای هر سمت.
          </p>
          <Button
            variant="primary"
            onClick={() => {
              setShowAddShiftModal(true);
            }}
          >
            افزودن شیفت جدید
          </Button>
        </div>

        {templates.length === 0 ? (
          <p className="muted">هنوز هیچ الگوی شیفتی تعریف نشده است.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>نام شیفت</th>
                  <th>ساعت کاری</th>
                  <th>حداقل پرسنل مورد نیاز</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((tpl) => (
                  <tr key={tpl.id}>
                    <td>
                      <div className="row" style={{ alignItems: "center", gap: "var(--s2)" }}>
                        <span
                          style={{
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            backgroundColor: tpl.color || "#3b82f6",
                          }}
                        />
                        <strong>{tpl.name}</strong>
                      </div>
                    </td>
                    <td>
                      {tpl.startTime} تا {tpl.endTime}
                    </td>
                    <td>
                      {tpl.minHeadcount.length > 0 ? (
                        tpl.minHeadcount
                          .map((m) => `${m.positionName || "سمت"}: ${String(m.minCount)} نفر`)
                          .join("، ")
                      ) : (
                        <span className="muted">نامحدود</span>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="urgent"
                        onClick={() => {
                          archiveShiftAction.run({ id: tpl.id, version: tpl.version, name: tpl.name });
                        }}
                      >
                        بایگانی
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* ---------- Weekly Pattern Grid ---------- */}
      <Panel title="برنامه هفتگی پرسنل (الگوی ثابت)">
        <p className="muted" style={{ margin: "0 0 var(--s3) 0" }}>
          تعیین شیفت کاری پیش‌فرض هر شخص در روزهای هفته (شنبه تا جمعه).
        </p>

        {staff.length === 0 ? (
          <p className="muted">ابتدا پرسنل را در تب پرونده پرسنل تعریف کنید.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>نام پرسنل</th>
                  <th>سمت</th>
                  {IRANIAN_DAYS.map((d) => (
                    <th key={d.dayOfWeek}>{d.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {staff.map((person) => {
                  const pat = patterns.find((p) => p.personId === person.personId);
                  const daysMap = new Map((pat?.days ?? []).map((d) => [d.dayOfWeek, d.shiftTemplateId]));

                  return (
                    <tr key={person.personId}>
                      <td>
                        <strong>{person.personName}</strong>
                      </td>
                      <td className="muted">{person.positionName || "—"}</td>
                      {IRANIAN_DAYS.map((d) => {
                        const currentShiftId = daysMap.get(d.dayOfWeek) ?? "";
                        return (
                          <td key={d.dayOfWeek}>
                            <select
                              value={currentShiftId}
                              style={{ padding: "4px 8px", borderRadius: "4px" }}
                              onChange={(e) => {
                                const newShiftId = e.target.value || null;
                                const newDays = IRANIAN_DAYS.map((day) => ({
                                  dayOfWeek: day.dayOfWeek,
                                  shiftTemplateId:
                                    day.dayOfWeek === d.dayOfWeek
                                      ? newShiftId
                                      : (daysMap.get(day.dayOfWeek) ?? null),
                                }));
                                savePatternAction.run({ personId: person.personId, days: newDays });
                              }}
                            >
                              <option value="">تعطیل</option>
                              {templates.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* ---------- Shift Date Exceptions ---------- */}
      <Panel title="استثنائات تاریخ (تعطیلی، جابه‌جایی و جایگزین)">
        <div
          className="row wrap"
          style={{ justifyContent: "space-between", alignItems: "center", marginBottom: "var(--s3)" }}
        >
          <p className="muted" style={{ margin: 0 }}>
            تغییرات شیفت برای تاریخ‌های خاص که بر الگوی هفتگی اولویت دارند.
          </p>
          <Button
            variant="primary"
            onClick={() => {
              const first = staff[0];
              if (first && !excPersonId) {
                setExcPersonId(first.personId);
              }
              setShowExceptionModal(true);
            }}
          >
            ثبت استثنای جدید
          </Button>
        </div>

        {exceptions.length === 0 ? (
          <p className="muted">هیچ استثنایی برای تاریخ‌ها ثبت نشده است.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>تاریخ</th>
                  <th>پرسنل</th>
                  <th>نوع استثنا</th>
                  <th>شیفت جایگزین</th>
                  <th>دلیل</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {exceptions.map((exc) => {
                  const person = staff.find((s: PersonnelFileRow) => s.personId === exc.personId);
                  const tpl = templates.find((t) => t.id === exc.shiftTemplateId);
                  return (
                    <tr key={exc.id}>
                      <td>{exc.date}</td>
                      <td>
                        <strong>{person?.personName || exc.personId}</strong>
                      </td>
                      <td>
                        {exc.kind === "off" ? (
                          <span className="chip" style={{ color: "var(--danger)" }}>
                            تعطیل
                          </span>
                        ) : exc.kind === "replacement" ? (
                          <span className="chip" style={{ color: "var(--accent)" }}>
                            جایگزین
                          </span>
                        ) : (
                          <span className="chip">تغییر شیفت</span>
                        )}
                      </td>
                      <td>{tpl ? tpl.name : "—"}</td>
                      <td className="muted">{exc.reason || "—"}</td>
                      <td>
                        <Button
                          variant="urgent"
                          onClick={() => {
                            archiveExceptionAction.run({ id: exc.id, version: exc.version });
                          }}
                        >
                          لغو
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Modal: Add Shift Template */}
      {showAddShiftModal && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: "550px" }}>
            <header className="modal-header">
              <h3>تعریف الگوی شیفت جدید</h3>
            </header>
            <div className="modal-body stack" style={{ gap: "var(--s3)" }}>
              <Field
                id="shift-name-input"
                label="نام شیفت (مثلاً صبح ۰۶:۰۰–۱۴:۰۰)"
                type="text"
                value={shiftName}
                onChange={(e) => {
                  setShiftName(e.target.value);
                }}
                placeholder="صبح ۰۶:۰۰–۱۴:۰۰"
              />

              <div className="row" style={{ gap: "var(--s3)" }}>
                <div style={{ flex: 1 }}>
                  <Field
                    id="shift-start-time"
                    label="ساعت شروع (HH:mm)"
                    type="time"
                    value={startTime}
                    onChange={(e) => {
                      setStartTime(e.target.value);
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Field
                    id="shift-end-time"
                    label="ساعت پایان (HH:mm)"
                    type="time"
                    value={endTime}
                    onChange={(e) => {
                      setEndTime(e.target.value);
                    }}
                  />
                </div>
              </div>

              <Field
                id="shift-color"
                label="رنگ نمایش در جدول"
                type="color"
                value={color}
                onChange={(e) => {
                  setColor(e.target.value);
                }}
                style={{ width: "80px", height: "40px", cursor: "pointer" }}
              />

              <div className="stack" style={{ gap: "var(--s2)" }}>
                <label className="label">حداقل پرسنل مورد نیاز برای هر سمت:</label>
                {positions.map((pos) => (
                  <div
                    key={pos.id}
                    className="row"
                    style={{ alignItems: "center", justifyContent: "space-between" }}
                  >
                    <span>{pos.name}:</span>
                    <input
                      type="number"
                      min="0"
                      max="50"
                      value={minCountMap[pos.id] ?? 0}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10) || 0;
                        setMinCountMap((prev) => ({ ...prev, [pos.id]: val }));
                      }}
                      style={{ width: "80px", textAlign: "center" }}
                    />
                  </div>
                ))}
              </div>

              <ErrorLine error={addShiftAction.error} />
            </div>
            <footer className="modal-footer row" style={{ justifyContent: "flex-end", gap: "var(--s2)" }}>
              <Button
                onClick={() => {
                  setShowAddShiftModal(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="primary"
                disabled={!shiftName.trim() || !startTime || !endTime || addShiftAction.busy}
                onClick={() => {
                  addShiftAction.run(undefined);
                }}
              >
                ثبت الگوی شیفت
              </Button>
            </footer>
          </div>
        </div>
      )}

      {/* Modal: Add Shift Exception */}
      {showExceptionModal && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: "500px" }}>
            <header className="modal-header">
              <h3>ثبت استثنای شیفت</h3>
            </header>
            <div className="modal-body stack" style={{ gap: "var(--s3)" }}>
              <div className="field">
                <label htmlFor="exc-person-select" className="label">
                  پرسنل
                </label>
                <select
                  id="exc-person-select"
                  className="input"
                  value={excPersonId}
                  onChange={(e) => {
                    setExcPersonId(e.target.value);
                  }}
                >
                  {staff.map((s: PersonnelFileRow) => (
                    <option key={s.personId} value={s.personId}>
                      {s.personName} ({s.positionName || "بدون سمت"})
                    </option>
                  ))}
                </select>
              </div>

              <Field
                id="exc-date"
                label="تاریخ استثنا (YYYY-MM-DD)"
                type="date"
                value={excDate}
                onChange={(e) => {
                  setExcDate(e.target.value);
                }}
              />

              <div className="field">
                <label htmlFor="exc-kind-select" className="label">
                  نوع استثنا
                </label>
                <select
                  id="exc-kind-select"
                  className="input"
                  value={excKind}
                  onChange={(e) => {
                    setExcKind(e.target.value as "off" | "shift" | "replacement");
                  }}
                >
                  <option value="off">تعطیلی / مرخصی شخص</option>
                  <option value="shift">تغییر شیفت این روز</option>
                  <option value="replacement">شیفت جایگزین برای همکار</option>
                </select>
              </div>

              {excKind !== "off" && (
                <div className="field">
                  <label htmlFor="exc-shift-select" className="label">
                    شیفت کاری
                  </label>
                  <select
                    id="exc-shift-select"
                    className="input"
                    value={excShiftId}
                    onChange={(e) => {
                      setExcShiftId(e.target.value);
                    }}
                  >
                    <option value="">انتخاب شیفت...</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {excKind === "replacement" && (
                <div className="field">
                  <label htmlFor="exc-replacement-select" className="label">
                    جایگزین چه کسی است؟
                  </label>
                  <select
                    id="exc-replacement-select"
                    className="input"
                    value={excReplacementId}
                    onChange={(e) => {
                      setExcReplacementId(e.target.value);
                    }}
                  >
                    <option value="">انتخاب همکار...</option>
                    {staff
                      .filter((s: PersonnelFileRow) => s.personId !== excPersonId)
                      .map((s: PersonnelFileRow) => (
                        <option key={s.personId} value={s.personId}>
                          {s.personName}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <Field
                id="exc-reason"
                label="دلیل / توضیحات"
                type="text"
                value={excReason}
                onChange={(e) => {
                  setExcReason(e.target.value);
                }}
                placeholder="مثال: جابه‌جایی توافقی با سرآشپز"
              />

              <ErrorLine error={addExceptionAction.error} />
            </div>
            <footer className="modal-footer row" style={{ justifyContent: "flex-end", gap: "var(--s2)" }}>
              <Button
                onClick={() => {
                  setShowExceptionModal(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="primary"
                disabled={!excPersonId || !excDate || addExceptionAction.busy}
                onClick={() => {
                  addExceptionAction.run(undefined);
                }}
              >
                ثبت استثنا
              </Button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
